import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash, Plus, X, GithubLogo } from '@phosphor-icons/react';

const HerokuTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', git_url: '' });

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`/api/heroku/templates`, { withCredentials: true });
      setTemplates(res.data);
    } catch (e) {
      toast.error("Failed to fetch templates");
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`/api/heroku/templates`, form, { withCredentials: true });
      toast.success("Template added successfully");
      setShowAdd(false);
      setForm({ name: '', git_url: '' });
      fetchTemplates();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : (typeof detail === 'string' ? detail : "Failed to add template");
      toast.error(msg);
    }
  };

  const removeTemplate = async (id) => {
    if (!window.confirm("Are you sure you want to delete this template?")) return;
    try {
      await axios.delete(`/api/heroku/templates/${id}`, { withCredentials: true });
      fetchTemplates();
      toast.success("Template removed");
    } catch (e) {
      toast.error("Failed to remove template");
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-black text-[#8b60ed] mb-2">GIT TEMPLATES</h1>
          <p className="text-slate-400">Save public Git URLs to use for deployments.</p>
        </div>
        <button 
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 bg-[#8b60ed] text-white px-4 py-2 font-bold hover:bg-[#724dd4] transition-colors"
        >
          <Plus size={20} /> Add Template
        </button>
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-white/20 p-8 w-full max-w-lg relative animate-in zoom-in-95 duration-200">
            <button onClick={() => setShowAdd(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white">
              <X size={24} />
            </button>
            <h2 className="text-2xl font-bold mb-6 text-[#8b60ed]">ADD NEW TEMPLATE</h2>
            <form onSubmit={handleAdd} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Template Name</label>
                <input required value={form.name} placeholder="e.g. Node.js Express App" onChange={(e) => setForm({...form, name: e.target.value})} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#8b60ed]" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Public Git URL</label>
                <input required type="text" placeholder="https://github.com/heroku/node-js-getting-started.git" value={form.git_url} onChange={(e) => setForm({...form, git_url: e.target.value.trim()})} className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#8b60ed] font-mono text-sm" />
              </div>
              <button type="submit" className="w-full bg-[#8b60ed] text-white font-bold py-3 mt-4 hover:bg-[#724dd4] transition-colors">
                SAVE TEMPLATE
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map(t => (
          <div key={t.id} className="border border-white/10 bg-card p-6 relative group flex flex-col gap-4">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-2">
                <GithubLogo size={24} className="text-[#8b60ed]" weight="fill" />
                <h3 className="text-lg font-bold">{t.name}</h3>
              </div>
              <button onClick={() => removeTemplate(t.id)} className="text-slate-400 hover:text-red-500 transition-colors p-1" title="Remove"><Trash size={20} /></button>
            </div>
            <div className="text-sm font-mono text-slate-400 break-all border-t border-white/10 pt-4 mt-auto">
              <a href={t.git_url} target="_blank" rel="noreferrer" className="hover:text-[#8b60ed] hover:underline">
                {t.git_url}
              </a>
            </div>
          </div>
        ))}
        {templates.length === 0 && (
          <div className="col-span-full p-8 border border-dashed border-white/20 text-center text-slate-500">
            No templates saved yet.
          </div>
        )}
      </div>
    </div>
  );
};

export default HerokuTemplates;